import React, { useState, useEffect } from 'react';
import HttpService from '../../service/HttpService'; // Import HttpService
import Table from './Table';

const PreloadSection = () => {
    const { axiosInstance } = HttpService(); // Use the secure axios instance
    const [view, setView] = useState('list'); // 'list' or 'create'
    const [sessions, setSessions] = useState([]); // Store preloaded sessions
    const [isLoading, setIsLoading] = useState(false); // Track loading state
    const [sessionTitle, setSessionTitle] = useState('');
    const [files, setFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [successData, setSuccessData] = useState(null); // Store API response data
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Track delete modal visibility
    const [sessionToDelete, setSessionToDelete] = useState(null); // Store session to delete


    // Fetch preloaded sessions
    const fetchSessions = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/scanning-sessions/listAll');
            setSessions(response.data);
        } catch (error) {
            console.error('Failed to fetch preloaded sessions:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (view === 'list') {
            fetchSessions();
        }
    }, [view]);

    const handleDeleteSession = async () => {
        if (!sessionToDelete) return;

        setIsLoading(true);
        try {
            await axiosInstance.delete(`/scanning-sessions/${sessionToDelete.id}`);
            setShowDeleteModal(false);
            fetchSessions(); // Reload the list after deletion
        } catch (error) {
            console.error('Failed to delete session:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTitleChange = (e) => {
        const value = e.target.value;
        if (value.length <= 20) {
            setSessionTitle(value);
        }
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files).filter(
            (file) => file.type === 'application/pdf'
        );
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = () => {
        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const droppedFiles = Array.from(e.dataTransfer.files).filter(
            (file) => file.type === 'application/pdf'
        );
        setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const validateFields = () => {
        const errors = {};
        if (!sessionTitle) {
            errors.sessionTitle = 'Session Title is required.';
        }
        if (files.length === 0) {
            errors.files = 'At least one PDF file must be selected.';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateFields()) {
            return;
        }

        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        formData.append('title', sessionTitle);

        setIsLoading(true); // Show loading indicator
        try {
            const response = await axiosInstance.post('/scanning-sessions/create', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Extract and format the response data
            const { title, createdDate, deliveryNotes } = response.data;
            const formattedDate = new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(new Date(createdDate));

            setSuccessData({ title, createdDate: formattedDate, deliveryNotes });
            setShowSuccessModal(true);
            setSessionTitle('');
            setFiles([]);
        } catch (error) {
            setShowErrorModal(true);
        } finally {
            setIsLoading(false); // Hide loading indicator
        }
    };

    const columns = [
        { key: 'title', label: 'Title' },
        {
            key: 'createdDate',
            label: 'Date',
            render: (createdDate) =>
                new Intl.DateTimeFormat('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }).format(new Date(createdDate)),
        },
        {
            key: 'deliveryNotes',
            label: 'Delivery Notes',
            render: (deliveryNotes) => deliveryNotes.join(', '),
        },
    ];

    const actions = [
        {
            label: 'Delete',
            className:
                'bg-red-100 text-red-600 px-3 py-1 rounded-lg hover:bg-red-200 transition',
            onClick: (session) => {
                setSessionToDelete(session);
                setShowDeleteModal(true);
            },
        },
    ];

    return (
        <section id="preloadSection" class="dashboard-section">
            {view === 'list' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-800">Preloaded Sessions</h2>
                        <button
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                            onClick={() => setView('create')}
                        >
                            Create Preloaded Session
                        </button>
                    </div>
                    {isLoading ? (
                        <div className="text-center">Loading...</div>
                    ) : (
                        <Table columns={columns} data={sessions} actions={actions} />
                    )}
                </div>
            )}

            {view === 'create' && (
                <div class="bg-white rounded-xl shadow-sm p-6">
                    <h2 class="text-xl font-semibold mb-6 text-gray-800">Create Preloaded Session</h2>
                    <form id="uploadForm" onSubmit={handleSubmit}>
                        <div class="mb-6">
                            <label class="block text-gray-700 font-medium mb-2">Session Title</label>
                            <input
                                type="text"
                                id="sessionTitle"
                                value={sessionTitle}
                                onChange={handleTitleChange}
                                class={`w-full px-4 py-2 border ${validationErrors.sessionTitle
                                    ? 'border-red-500'
                                    : 'border-gray-300'
                                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                placeholder="Enter a title for this scanning session (max 20 characters)"
                            />
                            {validationErrors.sessionTitle && (
                                <p class="text-red-500 text-sm mt-1">{validationErrors.sessionTitle}</p>
                            )}
                        </div>
                        <div class="mb-6">
                            <label class="block text-gray-700 font-medium mb-2">Upload PDF Files</label>
                            <div
                                class={`border-2 border-dashed rounded-lg p-8 text-center ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                                    }`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    id="fileUpload"
                                    class="hidden"
                                    accept=".pdf"
                                    multiple
                                    onChange={handleFileChange}
                                />
                                <i class="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-3"></i>
                                <p class="text-gray-500 mb-2">
                                    Drag & drop your PDF files here or click to browse
                                </p>
                                <button
                                    type="button"
                                    id="browseFilesBtn"
                                    class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                                    onClick={() => document.getElementById('fileUpload').click()}
                                >
                                    Select Files
                                </button>
                            </div>
                            {validationErrors.files && (
                                <p class="text-red-500 text-sm mt-1">{validationErrors.files}</p>
                            )}
                            {files.length > 0 && (
                                <div id="fileList" class="mt-4">
                                    <p class="font-medium text-gray-700 mb-2">Selected files:</p>
                                    <ul class="border border-gray-200 rounded-lg divide-y divide-gray-200">
                                        {files.map((file, index) => (
                                            <li
                                                key={index}
                                                class="px-4 py-2 flex justify-between items-center"
                                            >
                                                <span class="text-gray-700">{file.name}</span>
                                                <span class="text-gray-500 text-sm">
                                                    {(file.size / 1024).toFixed(2)} KB
                                                </span>
                                                <button
                                                    type="button"
                                                    class="bg-red-500 text-white px-2 py-1 rounded-lg hover:bg-red-600 transition"
                                                    onClick={() => handleRemoveFile(index)}
                                                >
                                                    Remove
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div class="flex space-x-4">
                            <button
                                class="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition"
                                onClick={() => setView('list')}
                            >
                                Back to List
                            </button>
                            <button
                                type="submit"
                                class="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
                                disabled={isLoading} // Disable button while loading
                            >
                                {isLoading ? 'Processing...' : 'Create Scanning Session'}
                            </button>
                        </div>
                    </form>


                </div>
            )}

            {/* Loading Overlay */}
            {isLoading && (
                <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin"></div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && successData && (
                <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="bg-white rounded-lg p-6 text-center">
                        <h3 class="text-lg font-semibold text-green-600 mb-4">Success</h3>
                        <p class="text-gray-700 mb-2">
                            <strong>Title:</strong> {successData.title}
                        </p>
                        <p class="text-gray-700 mb-2">
                            <strong>Created Date:</strong> {successData.createdDate}
                        </p>
                        <p class="text-gray-700 mb-4">
                            <strong>Delivery Notes:</strong>
                        </p>
                        <ul class="text-gray-700 list-disc list-inside">
                            {successData.deliveryNotes.map((note, index) => (
                                <li key={index}>{note}</li>
                            ))}
                        </ul>
                        <div class="mt-4 flex justify-center space-x-4">
                            <button
                                class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    setView('list');
                                }}
                            >
                                Go to Preloaded Sessions
                            </button>
                            <button
                                class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                onClick={() => setShowSuccessModal(false)}
                            >
                                Create More
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {showErrorModal && (
                <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="bg-white rounded-lg p-6 text-center">
                        <h3 class="text-lg font-semibold text-red-600 mb-4">Error</h3>
                        <p class="text-gray-700">Failed to create scanning session. Please try again.</p>
                        <button
                            class="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                            onClick={() => setShowErrorModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

             {/* Delete Confirmation Modal */}
             {showDeleteModal && sessionToDelete && (
                <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div class="bg-white rounded-lg p-6 text-center">
                        <h3 class="text-lg font-semibold text-red-600 mb-4">Confirm Deletion</h3>
                        <p class="text-gray-700 mb-4">
                            Are you sure you want to delete the session titled{' '}
                            <strong>{sessionToDelete.title}</strong>?
                        </p>
                        <div class="flex justify-center space-x-4">
                            <button
                                class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                class="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                                onClick={handleDeleteSession}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default PreloadSection;